import React, { useState } from "react";
import { useQuery, gql } from "@apollo/client";
import "../styles/BuyerCart.css";

const GET_ORDERS_WITH_RETURNS = gql`
  query GetOrdersWithReturns($status: String) {
    ordersWithReturns(status: $status) {
      orderId
      buyer {
        buyerId
        name
        email
        phone
      }
      payment {
        mode
        paymentStatus
      }
      items {
        productId
        sellerId
        name
        price
        quantity
        image
      }
      returns {
        productId
        sellerId
        reason
        status
        refundStatus
        refundAmount
        requestedAt
        approvedAt
        receivedAt
        refundedAt
        refundId
      }
      createdAt
    }
  }
`;

function ReturnProductsForm() {
  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'ascending' });
  const [page, setPage] = useState(1);
  const [showFilters, setShowFilters] = useState(false);
  const [dateFilter, setDateFilter] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const PAGE_SIZE = 50;

  // Fetch orders with returns
  const { data, loading, error, refetch } = useQuery(GET_ORDERS_WITH_RETURNS, {
    variables: { status: statusFilter || null },
    fetchPolicy: "network-only",
  });

  // Flatten orders → one row per return entry
  const returns = React.useMemo(() => {
    if (!data?.ordersWithReturns) return [];

    const rows = [];
    for (const order of data.ordersWithReturns) {
      if (!order.returns || order.returns.length === 0) continue;

      for (const ret of order.returns) {
        // Find the matching item for this return
        const matchedItem = order.items?.find(
          (item) => item.productId === ret.productId
        );

        rows.push({
          orderId: order.orderId,
          buyerId: order.buyer?.buyerId || "N/A",
          buyerName: order.buyer?.name || "N/A",
          buyerEmail: order.buyer?.email || "N/A",
          buyerPhone: order.buyer?.phone || "N/A",
          paymentMode: order.payment?.mode || "N/A",
          paymentStatus: order.payment?.paymentStatus || "N/A",
          productId: ret.productId,
          sellerId: ret.sellerId || "N/A",
          productName: matchedItem?.name || "N/A",
          productPrice: matchedItem?.price || 0,
          productQty: matchedItem?.quantity || 0,
          productImage: matchedItem?.image || "",
          reason: ret.reason || "N/A",
          status: ret.status || "N/A",
          refundStatus: ret.refundStatus || "N/A",
          refundAmount: ret.refundAmount || 0,
          requestedAt: ret.requestedAt,
          approvedAt: ret.approvedAt,
          receivedAt: ret.receivedAt,
          refundedAt: ret.refundedAt,
          refundId: ret.refundId || "N/A",
          createdAt: order.createdAt,
        });
      }
    }
    return rows;
  }, [data]);

  // Format date function
  const formatDate = (date) => {
    if (!date) return "N/A";
    return new Date(date).toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  // Get day name from date
  const getDayName = (date) => {
    if (!date) return "N/A";
    return new Date(date).toLocaleDateString('en-IN', { weekday: 'long' });
  };

  // Sorting function
  const sortedReturns = React.useMemo(() => {
    let sortableReturns = [...returns];
    if (sortConfig.key) {
      sortableReturns.sort((a, b) => {
        let aValue = a[sortConfig.key] || '';
        let bValue = b[sortConfig.key] || '';

        if (sortConfig.key === 'requestedAt' || sortConfig.key === 'createdAt') {
          aValue = new Date(a[sortConfig.key] || 0);
          bValue = new Date(b[sortConfig.key] || 0);
        } else if (sortConfig.key === 'refundAmount' || sortConfig.key === 'productPrice') {
          aValue = Number(aValue);
          bValue = Number(bValue);
        }

        if (aValue < bValue) {
          return sortConfig.direction === 'ascending' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'ascending' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableReturns;
  }, [returns, sortConfig]);

  // Request sort function
  const requestSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  // Filter returns function
  const filteredReturns = sortedReturns.filter((returnItem) => {
    const term = searchTerm.toLowerCase();
    
    // Text search filter
    if (term && !(
      returnItem.orderId?.toLowerCase().includes(term) ||
      returnItem.productId?.toLowerCase().includes(term) ||
      returnItem.productName?.toLowerCase().includes(term) ||
      returnItem.buyerId?.toLowerCase().includes(term) ||
      returnItem.buyerName?.toLowerCase().includes(term) ||
      returnItem.buyerEmail?.toLowerCase().includes(term) ||
      returnItem.sellerId?.toLowerCase().includes(term) ||
      returnItem.reason?.toLowerCase().includes(term) ||
      returnItem.refundId?.toLowerCase().includes(term)
    )) {
      return false;
    }

    // Date filter (today, yesterday, last 7 days, last 30 days, custom range)
    if (dateFilter || startDate || endDate) {
      const returnDate = new Date(returnItem.requestedAt || returnItem.createdAt);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      if (dateFilter === "today") {
        const returnDay = new Date(returnDate);
        returnDay.setHours(0, 0, 0, 0);
        if (returnDay.getTime() !== today.getTime()) return false;
      } 
      else if (dateFilter === "yesterday") {
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        const returnDay = new Date(returnDate);
        returnDay.setHours(0, 0, 0, 0);
        if (returnDay.getTime() !== yesterday.getTime()) return false;
      }
      else if (dateFilter === "last7days") {
        const weekAgo = new Date(today);
        weekAgo.setDate(weekAgo.getDate() - 7);
        if (returnDate < weekAgo) return false;
      }
      else if (dateFilter === "last30days") {
        const monthAgo = new Date(today);
        monthAgo.setDate(monthAgo.getDate() - 30);
        if (returnDate < monthAgo) return false;
      }
      else if (dateFilter === "thisweek") {
        const firstDayOfWeek = new Date(today);
        const day = today.getDay();
        const diff = today.getDate() - day + (day === 0 ? -6 : 1);
        firstDayOfWeek.setDate(diff);
        firstDayOfWeek.setHours(0, 0, 0, 0);
        if (returnDate < firstDayOfWeek) return false;
      }
      else if (dateFilter === "thismonth") {
        const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
        if (returnDate < firstDayOfMonth) return false;
      }
    }

    // Custom date range filter
    if (startDate || endDate) {
      const returnDate = new Date(returnItem.requestedAt || returnItem.createdAt).getTime();
      
      if (startDate) {
        const start = new Date(startDate).getTime();
        if (returnDate < start) return false;
      }
      
      if (endDate) {
        const end = new Date(endDate);
        end.setHours(23, 59, 59, 999);
        if (returnDate > end.getTime()) return false;
      }
    }

    return true;
  });

  // Pagination calculations
  const totalPages = Math.ceil(filteredReturns.length / PAGE_SIZE);
  const startIndex = (page - 1) * PAGE_SIZE;
  const endIndex = Math.min(startIndex + PAGE_SIZE, filteredReturns.length);
  const paginatedReturns = filteredReturns.slice(startIndex, endIndex);

  // Show "Next" page if there are more items
  const hasNextPage = filteredReturns.length > page * PAGE_SIZE;
  const hasPreviousPage = page > 1;

  // Reset all filters
  const resetFilters = () => {
    setDateFilter("");
    setStartDate("");
    setEndDate("");
    setStatusFilter("");
    setPage(1);
  };

  // Handle refresh
  const handleRefresh = () => {
    refetch();
  };

  // Export to CSV function
  const exportToCSV = () => {
    if (filteredReturns.length === 0) {
      alert("No data to export!");
      return;
    }

    // Prepare CSV headers
    const headers = [
      'Order ID',
      'Requested Date',
      'Day',
      'Product Name',
      'Product ID',
      'Price (₹)',
      'Qty',
      'Refund Amount (₹)',
      'Return Status',
      'Refund Status',
      'Refund ID',
      'Buyer ID',
      'Buyer Name',
      'Buyer Email',
      'Buyer Phone',
      'Seller ID',
      'Payment Mode',
      'Payment Status',
      'Reason'
    ];

    // Prepare CSV rows
    const csvRows = filteredReturns.map(returnItem => {
      return [
        `"${returnItem.orderId || ''}"`,
        `"${formatDate(returnItem.requestedAt)}"`,
        `"${getDayName(returnItem.requestedAt)}"`,
        `"${returnItem.productName || ''}"`,
        `"${returnItem.productId || ''}"`,
        (returnItem.productPrice || 0).toFixed(2),
        returnItem.productQty || 0,
        (returnItem.refundAmount || 0).toFixed(2),
        `"${returnItem.status || ''}"`,
        `"${returnItem.refundStatus || ''}"`,
        `"${returnItem.refundId || ''}"`,
        `"${returnItem.buyerId || ''}"`,
        `"${returnItem.buyerName || ''}"`,
        `"${returnItem.buyerEmail || ''}"`,
        `"${returnItem.buyerPhone || ''}"`,
        `"${returnItem.sellerId || ''}"`,
        `"${returnItem.paymentMode || ''}"`,
        `"${returnItem.paymentStatus || ''}"`,
        `"${returnItem.reason || ''}"`
      ];
    });

    // Combine headers and rows
    const csvContent = [
      headers.join(','),
      ...csvRows.map(row => row.join(','))
    ].join('\n');

    // Create and download CSV file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `return_products_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Export to JSON function
  const exportToJSON = () => {
    if (filteredReturns.length === 0) {
      alert("No data to export!");
      return;
    }

    const dataStr = JSON.stringify(filteredReturns, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
    const link = document.createElement('a');
    link.setAttribute('href', dataUri);
    link.setAttribute('download', 'return_products.json');
    link.click();
  };

  // Calculate totals
  const totalRefundAmount = filteredReturns.reduce((sum, returnItem) => 
    sum + (returnItem.refundAmount || 0), 0
  );
  const totalItems = filteredReturns.length;

  // Status badge color helper
  const getStatusClass = (status) => {
    const s = (status || '').toUpperCase();
    if (s === 'REFUNDED' || s === 'COMPLETED') return 'completed';
    if (s === 'APPROVED' || s === 'QC_PASSED') return 'approved';
    if (s === 'REJECTED' || s === 'QC_FAILED') return 'rejected';
    if (s === 'RECEIVED') return 'received';
    return 'pending';
  };

  return (
    <div className="sheet-container">
      <div className="sheet-header">
        <h1>
          <span className="sheet-icon">📦</span>
          Return Products Management
        </h1>
        <div className="sheet-actions">
          <div className="search-box">
            <input
              type="text"
              placeholder="Search by order, product, buyer, seller..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setPage(1);
              }}
              className="sheet-search"
            />
          
          </div>
          <div className="action-buttons">
            <button 
              className="filter-btn" 
              onClick={() => setShowFilters(!showFilters)}
            >
              {showFilters ? '▲ Hide Filters' : '▼ Show Filters'}
            </button>
            <button className="refresh-btn" onClick={handleRefresh}>
              ↻ Refresh
            </button>
            <div className="export-dropdown">
              <button className="export-btn">
                📥 Export
              </button>
              <div className="export-menu">
                <button onClick={exportToCSV}>Export as CSV</button>
                <button onClick={exportToJSON}>Export as JSON</button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters Section */}
      {showFilters && (
        <div className="filters-container">
          <div className="filters-header">
            <h3>Advanced Filters</h3>
            <button className="reset-filters-btn" onClick={resetFilters}>
              Reset All Filters
            </button>
          </div>
          
          <div className="filters-grid">
            {/* Date Filters */}
            <div className="filter-group">
              <label>Date Filter</label>
              <select 
                value={dateFilter} 
                onChange={(e) => {
                  setDateFilter(e.target.value);
                  setPage(1);
                }}
                className="filter-select"
              >
                <option value="">All Dates</option>
                <option value="today">Today</option>
                <option value="yesterday">Yesterday</option>
                <option value="thisweek">This Week</option>
                <option value="thismonth">This Month</option>
                <option value="last7days">Last 7 Days</option>
                <option value="last30days">Last 30 Days</option>
              </select>
            </div>

            {/* Custom Date Range */}
            <div className="filter-group">
              <label>Custom Date Range</label>
              <div className="date-range-inputs">
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => {
                    setStartDate(e.target.value);
                    setPage(1);
                  }}
                  className="date-input"
                  placeholder="Start Date"
                />
                <span className="date-separator">to</span>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => {
                    setEndDate(e.target.value);
                    setPage(1);
                  }}
                  className="date-input"
                  placeholder="End Date"
                />
              </div>
            </div>

            {/* Status Filter */}
            <div className="filter-group">
              <label>Return Status</label>
              <select 
                value={statusFilter} 
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setPage(1);
                }}
                className="filter-select"
              >
                <option value="">All Status</option>
                <option value="REQUESTED">Requested</option>
                <option value="APPROVED">Approved</option>
                <option value="REJECTED">Rejected</option>
                <option value="RECEIVED">Received</option>
                <option value="QC_PASSED">QC Passed</option>
                <option value="QC_FAILED">QC Failed</option>
                <option value="REFUND_INITIATED">Refund Initiated</option>
                <option value="REFUNDED">Refunded</option>
              </select>
            </div>
          </div>
        </div>
      )}

      <div className="sheet-summary">
        <div className="summary-card">
          <span className="summary-label">Total Returns</span>
          <span className="summary-value">{filteredReturns.length}</span>
        </div>
        <div className="summary-card">
          <span className="summary-label">Total Items</span>
          <span className="summary-value">{totalItems}</span>
        </div>
        <div className="summary-card">
          <span className="summary-label">Total Refund Value</span>
          <span className="summary-value">₹{totalRefundAmount.toFixed(2)}</span>
        </div>
        <div className="summary-card">
          <span className="summary-label">Showing</span>
          <span className="summary-value">
            {filteredReturns.length > 0 ? `${startIndex + 1}-${endIndex}` : '0'} of {filteredReturns.length}
          </span>
        </div>
      </div>

      {/* Loading & Error States */}
      {loading && (
        <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
          <p>⏳ Loading return orders...</p>
        </div>
      )}

      {error && (
        <div style={{ textAlign: 'center', padding: '40px', color: '#e74c3c' }}>
          <p>❌ Error: {error.message}</p>
          <button className="refresh-btn" onClick={handleRefresh}>Try Again</button>
        </div>
      )}

      {!loading && !error && (
        <div className="sheet-table-container">
          <div className="sheet-table-wrapper">
            <table className="sheet-table">
              <thead>
                <tr>
                  <th 
                    className="sortable"
                    onClick={() => requestSort('orderId')}
                  >
                    Order ID
                    {sortConfig.key === 'orderId' && (
                      <span className="sort-indicator">
                        {sortConfig.direction === 'ascending' ? '↑' : '↓'}
                      </span>
                    )}
                  </th>
                  <th 
                    className="sortable"
                    onClick={() => requestSort('requestedAt')}
                  >
                    Requested Date
                    {sortConfig.key === 'requestedAt' && (
                      <span className="sort-indicator">
                        {sortConfig.direction === 'ascending' ? '↑' : '↓'}
                      </span>
                    )}
                  </th>
                  <th>Day</th>
                  <th 
                    className="sortable"
                    onClick={() => requestSort('productName')}
                  >
                    Product
                    {sortConfig.key === 'productName' && (
                      <span className="sort-indicator">
                        {sortConfig.direction === 'ascending' ? '↑' : '↓'}
                      </span>
                    )}
                  </th>
                  <th>Product ID</th>
                  <th 
                    className="sortable numeric"
                    onClick={() => requestSort('refundAmount')}
                  >
                    Refund (₹)
                    {sortConfig.key === 'refundAmount' && (
                      <span className="sort-indicator">
                        {sortConfig.direction === 'ascending' ? '↑' : '↓'}
                      </span>
                    )}
                  </th>
                  <th>Return Status</th>
                  <th>Refund Status</th>
                  <th>Customer (Buyer ID)</th>
                  <th>Seller (Seller ID)</th>
                  <th>Payment</th>
                  <th>Reason</th>
                  <th>Refund ID</th>
                </tr>
              </thead>
              <tbody>
                {paginatedReturns.length === 0 ? (
                  <tr>
                    <td colSpan="13" className="no-data-cell">
                      <div className="empty-state">
                        <span className="empty-icon">📭</span>
                        <p>No return products found</p>
                        {searchTerm && <p className="empty-subtext">No results for "{searchTerm}"</p>}
                      </div>
                    </td>
                  </tr>
                ) : (
                  paginatedReturns.map((returnItem, index) => {
                    return (
                      <tr key={`${returnItem.orderId}-${returnItem.productId}-${index}`}>
                        <td className="return-cell">
                          <span className="cell-value">{returnItem.orderId}</span>
                        </td>
                        <td className="date-cell">
                          <span className="cell-value">{formatDate(returnItem.requestedAt)}</span>
                        </td>
                        <td className="day-cell">
                          <span className="cell-value">{getDayName(returnItem.requestedAt)}</span>
                        </td>
                        <td className="product-cell">
                          <div className="product-info">
                            {returnItem.productImage && (
                              <img 
                                src={returnItem.productImage} 
                                alt={returnItem.productName} 
                                style={{ width: 36, height: 36, objectFit: 'cover', borderRadius: 4, marginRight: 8 }}
                              />
                            )}
                            <div>
                              <div className="product-name">{returnItem.productName}</div>
                              <div style={{ fontSize: '11px', color: '#888' }}>₹{returnItem.productPrice} × {returnItem.productQty}</div>
                            </div>
                          </div>
                        </td>
                        <td>
                          <span className="product-id">{returnItem.productId}</span>
                        </td>
                        <td className="numeric">
                          <span className="refund-value">₹{(returnItem.refundAmount || 0).toFixed(2)}</span>
                        </td>
                        <td>
                          <span className={`status-badge ${getStatusClass(returnItem.status)}`}>
                            {returnItem.status}
                          </span>
                        </td>
                        <td>
                          <span className={`status-badge ${getStatusClass(returnItem.refundStatus)}`}>
                            {returnItem.refundStatus}
                          </span>
                        </td>
                        {/* Customer Area - shows buyerId */}
                        <td>
                          <div className="customer-info">
                            <div className="customer-name" style={{ fontWeight: 600 }}>{returnItem.buyerId}</div>
                            <div className="customer-contact">{returnItem.buyerName}</div>
                            <div className="customer-contact">{returnItem.buyerEmail}</div>
                            <div className="customer-contact">{returnItem.buyerPhone}</div>
                          </div>
                        </td>
                        {/* Seller Area - shows sellerId */}
                        <td>
                          <div className="seller-info">
                            <div className="seller-name" style={{ fontWeight: 600 }}>{returnItem.sellerId}</div>
                          </div>
                        </td>
                        <td>
                          <div>
                            <div style={{ fontSize: '12px' }}>{returnItem.paymentMode}</div>
                            <div style={{ fontSize: '11px', color: returnItem.paymentStatus === 'PAID' ? '#27ae60' : '#e67e22' }}>
                              {returnItem.paymentStatus}
                            </div>
                          </div>
                        </td>
                        <td>
                          <span className="reason-text">{returnItem.reason}</span>
                        </td>
                        <td>
                          <span style={{ fontSize: '11px', wordBreak: 'break-all' }}>{returnItem.refundId}</span>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Simple Pagination */}
      {filteredReturns.length > PAGE_SIZE && (
        <div className="simple-pagination">
          <button
            className="pagination-btn"
            disabled={!hasPreviousPage}
            onClick={() => setPage(p => p - 1)}
          >
            Previous
          </button>
          
          <div className="page-info">
            Page {page} of {totalPages}
            <div className="showing-info">
              Showing {startIndex + 1}-{endIndex} of {filteredReturns.length} returns
            </div>
          </div>
          
          <button
            className="pagination-btn"
            disabled={!hasNextPage}
            onClick={() => setPage(p => p + 1)}
          >
            Next
          </button>
        </div>
      )}

      {/* Show pagination info even when there are less than 10 items but multiple pages */}
      {filteredReturns.length > 0 && filteredReturns.length <= PAGE_SIZE && totalPages > 1 && (
        <div className="simple-pagination">
          <button
            className="pagination-btn"
            disabled={!hasPreviousPage}
            onClick={() => setPage(p => p - 1)}
          >
            Previous
          </button>
          
          <div className="page-info">
            Page {page} of {totalPages}
          </div>
          
          <button
            className="pagination-btn"
            disabled={!hasNextPage}
            onClick={() => setPage(p => p + 1)}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}

export default ReturnProductsForm;